import { useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Corpo,
  Linha,
  Tabela,
} from "../componentes/ui";
import { ConviteDePessoa } from "../componentes/acesso/ConviteDePessoa";
import { SessoesAbertas } from "../componentes/acesso/SessoesAbertas";
import { TelasDaPessoa } from "../componentes/acesso/TelasDaPessoa";
import { usePapeisAtribuiveis, useRedefinirMfaDaPessoa } from "../nucleo/acesso";
import { mensagemDoConsole } from "../nucleo/console";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { resumoDaPessoa, useMatrizDeTelas, type MatrizDeTelas } from "../nucleo/telas";
import { useTitulo } from "../nucleo/titulo";
import {
  CATEGORIAS_DO_HISTORICO,
  categoriaDoRegistro,
  fraseDaAcao,
  useAlterarAtivacao,
  useAlterarPerfil,
  useAlterarSuperior,
  useCancelarConvite,
  useConvitesDaConta,
  useHistoricoDaConta,
  useUsuariosDaConta,
} from "../nucleo/usuarios";

/* Os quatro papéis fixos do cliente: o que a tela oferece enquanto (ou se) a lista de perfis do
   servidor não chega. Com ela, entram também os perfis personalizados que a GEOvendas criou. */
const PERFIS_FIXOS: readonly { valor: string; rotulo: string }[] = [
  { valor: "admin_cliente", rotulo: "Administrador" },
  { valor: "key_user", rotulo: "Key user" },
  { valor: "ti", rotulo: "TI" },
  { valor: "consulta", rotulo: "Consulta" },
];

/** Perfis que dá para escolher: os fixos com o rótulo de sempre e os personalizados do servidor. */
function usePerfisEscolhiveis(): readonly { valor: string; rotulo: string }[] {
  const papeis = usePapeisAtribuiveis();
  const personalizados = (papeis.data ?? [])
    .filter((papel) => papel.personalizado && papel.lado === "cliente")
    .map((papel) => ({ valor: papel.chave, rotulo: papel.nome }));
  return [...PERFIS_FIXOS, ...personalizados];
}

function dataHora(valor: string | null): string {
  if (!valor) return "nunca";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

function detalheLegivel(detalhe: Record<string, unknown>): string {
  return Object.entries(detalhe)
    .filter(([, valor]) => valor !== null && valor !== "" && valor !== false)
    .map(
      ([chave, valor]) =>
        `${chave.replaceAll("_", " ")}: ${Array.isArray(valor) ? valor.join(", ") : String(valor)}`,
    )
    .join(" · ");
}

export function UsuariosDaConta() {
  useTitulo("Usuários");
  const sessao = useSessao();
  const podeGerenciar = podeNaSessao(sessao.data, "usuarios:gerenciar");

  if (!podeGerenciar) {
    return (
      <Aviso tom="marca" data-testid="usuarios-sem-permissao">
        A administração dos acessos fica com quem é administrador da conta.
      </Aviso>
    );
  }

  return <ConteudoDosUsuarios />;
}

function ConteudoDosUsuarios() {
  const matriz = useMatrizDeTelas("cliente");
  const perfisEscolhiveis = usePerfisEscolhiveis();
  return (
    <div data-testid="usuarios-da-conta">
      <h1 className="titulo-pagina">Usuários</h1>
      <p className="lead">
        Quem entra pela sua empresa, o que pode acompanhar e o histórico de uso da conta.
      </p>
      <ConviteDePessoa
        perfis={perfisEscolhiveis}
        perfilInicial="key_user"
        matriz={matriz.data}
      />
      <ListaDeUsuarios matriz={matriz.data} perfis={perfisEscolhiveis} />
      <ConvitesPendentes />
      <HistoricoDeAcessos />
    </div>
  );
}

function ListaDeUsuarios({
  matriz,
  perfis: perfisEscolhiveis,
}: {
  matriz: MatrizDeTelas | undefined;
  perfis: readonly { valor: string; rotulo: string }[];
}) {
  const usuarios = useUsuariosDaConta();
  const alterarPerfil = useAlterarPerfil();
  const alterarAtivacao = useAlterarAtivacao();
  const alterarSuperior = useAlterarSuperior();
  const eu = useSessao().data?.usuario.id;
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [comPermissoes, setComPermissoes] = useState<string | null>(null);
  const pessoaSelecionada = usuarios.data?.find((usuario) => usuario.id === selecionado);
  const telasDaPessoa = matriz?.pessoas.find((pessoa) => pessoa.usuario.id === comPermissoes);
  const rotuloDoPerfil = (chave: string) =>
    perfisEscolhiveis.find((perfil) => perfil.valor === chave)?.rotulo ?? chave;

  return (
    <section className="mb-6" aria-labelledby="titulo-usuarios-da-conta">
      <h2 className="titulo-secao" id="titulo-usuarios-da-conta">Pessoas da conta</h2>
      {usuarios.isPending ? (
        <p role="status">Carregando usuários…</p>
      ) : usuarios.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(usuarios.error)}</Aviso>
      ) : (
        <Tabela legenda="Usuários da empresa, seus perfis e o último acesso.">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Pessoa</CelulaCabecalho>
              <CelulaCabecalho>Perfil</CelulaCabecalho>
              <CelulaCabecalho>Telas</CelulaCabecalho>
              <CelulaCabecalho>Superior</CelulaCabecalho>
              <CelulaCabecalho>Último acesso</CelulaCabecalho>
              <CelulaCabecalho>Acesso</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {usuarios.data.map((usuario) => (
              <Linha key={usuario.id} data-testid={`usuario-${usuario.id}`}>
                <Celula>
                  <b>{usuario.nome}</b>
                  <div className="celula-secundaria">
                    {usuario.email}
                    {usuario.cargo ? ` · ${usuario.cargo}` : ""}
                    {usuario.telefone ? ` · ${usuario.telefone}` : ""}
                  </div>
                </Celula>
                <Celula>
                  <select
                    className="campo__controle"
                    aria-label={`Perfil de ${usuario.nome}`}
                    value={usuario.papeis[0] ?? "consulta"}
                    disabled={!usuario.ativo || alterarPerfil.isPending}
                    onChange={(evento) =>
                      alterarPerfil.mutate({
                        usuarioId: usuario.id,
                        papel: evento.target.value,
                      })
                    }
                  >
                    {perfisEscolhiveis.map((perfil) => (
                      <option key={perfil.valor} value={perfil.valor}>
                        {perfil.rotulo}
                      </option>
                    ))}
                    {/* Perfil que a pessoa tem e que a lista não traz (ex.: lista ainda carregando)
                        continua visível, em vez de a seleção mentir "Consulta". */}
                    {usuario.papeis[0] && !perfisEscolhiveis.some((perfil) => perfil.valor === usuario.papeis[0]) ? (
                      <option value={usuario.papeis[0]}>{usuario.papeis[0]}</option>
                    ) : null}
                  </select>
                </Celula>
                <Celula className="celula-secundaria">
                  {matriz ? resumoDaPessoa(matriz, usuario.id) : "—"}
                </Celula>
                <Celula>
                  <select
                    className="campo__controle"
                    aria-label={`Superior de ${usuario.nome}`}
                    value={usuario.superior_id ?? ""}
                    disabled={!usuario.ativo || alterarSuperior.isPending}
                    onChange={(evento) =>
                      alterarSuperior.mutate({
                        usuarioId: usuario.id,
                        superiorId: evento.target.value || null,
                      })
                    }
                  >
                    <option value="">Sem superior</option>
                    {usuarios.data
                      .filter((colega) => colega.ativo && colega.id !== usuario.id)
                      .map((colega) => (
                        <option key={colega.id} value={colega.id}>
                          {colega.nome}
                        </option>
                      ))}
                  </select>
                </Celula>
                <Celula className="celula-secundaria">{dataHora(usuario.ultimo_acesso_em)}</Celula>
                <Celula>
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    disabled={alterarAtivacao.isPending}
                    onClick={() =>
                      alterarAtivacao.mutate({ usuarioId: usuario.id, ativo: !usuario.ativo })
                    }
                  >
                    {usuario.ativo ? "Desativar" : "Reativar"}
                  </Botao>{" "}
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    onClick={() => setSelecionado(selecionado === usuario.id ? null : usuario.id)}
                    aria-label={`Segurança de ${usuario.nome}`}
                    data-testid={`seguranca-${usuario.id}`}
                  >
                    Segurança
                  </Botao>{" "}
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    disabled={!matriz}
                    onClick={() => setComPermissoes(comPermissoes === usuario.id ? null : usuario.id)}
                    aria-label={`Permissões de ${usuario.nome}`}
                    data-testid={`permissoes-${usuario.id}`}
                  >
                    Permissões
                  </Botao>
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
      {telasDaPessoa && matriz ? (
        <TelasDaPessoa
          key={telasDaPessoa.usuario.id}
          pessoa={telasDaPessoa}
          matriz={matriz}
          nomeDoPerfil={rotuloDoPerfil}
        />
      ) : null}
      {pessoaSelecionada ? (
        <SegurancaDaPessoa
          key={pessoaSelecionada.id}
          pessoa={pessoaSelecionada}
          ehVoce={pessoaSelecionada.id === eu}
        />
      ) : null}
      {alterarPerfil.isError || alterarAtivacao.isError || alterarSuperior.isError ? (
        <Aviso tom="erro" className="mt-3">
          {mensagemDoConsole(alterarPerfil.error ?? alterarAtivacao.error ?? alterarSuperior.error)}
        </Aviso>
      ) : null}
    </section>
  );
}

function SegurancaDaPessoa({
  pessoa,
  ehVoce,
}: {
  pessoa: { id: string; nome: string; mfa_ativo: boolean };
  ehVoce: boolean;
}) {
  const redefinir = useRedefinirMfaDaPessoa(pessoa.id);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="mt-4" data-testid="seguranca-da-pessoa">
      <h3 className="titulo-secao">Segurança de {pessoa.nome}</h3>
      <p className="celula-secundaria mb-2">
        Verificação em duas etapas: <b>{pessoa.mfa_ativo ? "ligada" : "desligada"}</b>.
      </p>
      <SessoesAbertas dono={{ tipo: "pessoa", usuarioId: pessoa.id }} />
      {pessoa.mfa_ativo && !ehVoce ? (
        <div className="mt-4">
          {confirmando ? (
            <Aviso tom="atencao" data-testid="confirmar-redefinicao">
              Redefinir apaga o segundo fator e os códigos de recuperação de {pessoa.nome} e encerra
              as sessões dela. Ela entra de novo só com a senha (e configura o aplicativo outra vez,
              se a política exigir).
              <div className="mt-2 flex gap-2">
                <Botao
                  tamanho="pequeno"
                  disabled={redefinir.isPending}
                  onClick={() => redefinir.mutate(undefined, { onSuccess: () => setConfirmando(false) })}
                  data-testid="confirmar-redefinir-mfa"
                >
                  {redefinir.isPending ? "Redefinindo…" : "Redefinir"}
                </Botao>
                <Botao tamanho="pequeno" variante="fantasma" onClick={() => setConfirmando(false)}>
                  Cancelar
                </Botao>
              </div>
            </Aviso>
          ) : (
            <Botao
              variante="fantasma"
              onClick={() => setConfirmando(true)}
              data-testid="redefinir-mfa"
            >
              Redefinir a verificação em duas etapas
            </Botao>
          )}
          {redefinir.isError ? (
            <Aviso tom="erro" className="mt-3">
              {mensagemDoConsole(redefinir.error)}
            </Aviso>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ConvitesPendentes() {
  const convites = useConvitesDaConta();
  const cancelar = useCancelarConvite();
  const perfis = usePerfisEscolhiveis();
  const rotuloDoPerfil: Record<string, string> = Object.fromEntries(
    perfis.map((item) => [item.valor, item.rotulo]),
  );
  if (convites.isPending) return <p role="status">Carregando convites…</p>;
  if (convites.isError) return <Aviso tom="erro">{mensagemDoConsole(convites.error)}</Aviso>;
  if (convites.data.length === 0) return null;

  return (
    <section className="mb-6" aria-labelledby="titulo-convites-pendentes">
      <h2 className="titulo-secao" id="titulo-convites-pendentes">
        Convites pendentes
      </h2>
      <Tabela legenda="Convites que ainda podem ser aceitos.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>E-mail</CelulaCabecalho>
            <CelulaCabecalho>Perfil</CelulaCabecalho>
            <CelulaCabecalho>Expira</CelulaCabecalho>
            <CelulaCabecalho>Ação</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {convites.data.map((convite) => (
            <Linha key={convite.id} data-testid={`convite-${convite.id}`}>
              <Celula>{convite.email}</Celula>
              <Celula>{rotuloDoPerfil[convite.papel_chave] ?? convite.papel_chave}</Celula>
              <Celula>{dataHora(convite.expira_em)}</Celula>
              <Celula>
                <Botao
                  tamanho="pequeno"
                  variante="fantasma"
                  disabled={cancelar.isPending}
                  onClick={() => cancelar.mutate(convite.id)}
                >
                  Cancelar
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Corpo>
      </Tabela>
      {cancelar.isError ? (
        <Aviso tom="erro" className="mt-3">
          {mensagemDoConsole(cancelar.error)}
        </Aviso>
      ) : null}
    </section>
  );
}

function HistoricoDeAcessos() {
  const historico = useHistoricoDaConta();
  const [categoria, setCategoria] = useState("Todas");
  const [pagina, setPagina] = useState(1);
  const registros = (historico.data ?? []).filter(
    (registro) => categoria === "Todas" || categoriaDoRegistro(registro.acao) === categoria,
  );
  const paginas = Math.ceil(registros.length / 5);
  const paginaAtual = Math.min(pagina, Math.max(1, paginas));
  const registrosDaPagina = registros.slice((paginaAtual - 1) * 5, paginaAtual * 5);

  return (
    <section className="mb-6" aria-labelledby="titulo-historico-de-acessos">
      <h2 className="titulo-secao" id="titulo-historico-de-acessos">
        Histórico de acessos
      </h2>
      <p className="lead">
        Entradas e ações feitas na conta, visíveis para a sua empresa e para a GEOvendas.
      </p>
      <label className="campo__rotulo" htmlFor="categoria-do-historico">
        Categoria
      </label>
      <select
        id="categoria-do-historico"
        className="campo__controle mb-3"
        value={categoria}
        onChange={(evento) => {
          setCategoria(evento.target.value);
          setPagina(1);
        }}
        data-testid="filtro-do-historico"
      >
        {CATEGORIAS_DO_HISTORICO.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
      {historico.isPending ? (
        <p role="status">Carregando histórico…</p>
      ) : historico.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(historico.error)}</Aviso>
      ) : registros.length === 0 ? (
        <Aviso tom="marca">Nenhum registro nesta categoria.</Aviso>
      ) : (
        <Tabela
          legenda="Histórico de acessos e ações da conta."
          data-testid="historico-de-acessos"
        >
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Quando</CelulaCabecalho>
              <CelulaCabecalho>Quem</CelulaCabecalho>
              <CelulaCabecalho>Categoria</CelulaCabecalho>
              <CelulaCabecalho>Ação</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {registrosDaPagina.map((registro) => (
              <Linha key={registro.id} data-testid={`historico-${registro.id}`}>
                <Celula className="numero-tabular">{dataHora(registro.ocorrido_em)}</Celula>
                <Celula>{registro.ator_email ?? "Sistema"}</Celula>
                <Celula>{categoriaDoRegistro(registro.acao)}</Celula>
                <Celula>
                  {fraseDaAcao(registro.acao)}
                  <div className="celula-secundaria">{detalheLegivel(registro.detalhe)}</div>
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
      {paginas > 1 ? (
        <nav aria-label="Páginas do histórico de acessos" className="mt-3 flex items-center gap-3">
          <button type="button" className="botao botao--fantasma" disabled={paginaAtual === 1} onClick={() => setPagina(paginaAtual - 1)}>Anterior</button>
          <span role="status">Página {paginaAtual} de {paginas} · {registros.length} registros</span>
          <button type="button" className="botao botao--fantasma" disabled={paginaAtual === paginas} onClick={() => setPagina(paginaAtual + 1)}>Próxima</button>
        </nav>
      ) : null}
    </section>
  );
}
